import React, { FC, useState } from 'react'
import { GetStaticProps } from 'next'
import {
  getPageByUrlSlug,
  SingleItemResponse,
  MultipleItemResponse,
  getAllRolexRetailers,
  getKontentImage
} from '@lib/kontent'
import { RolexContactPage } from '@lib/kontent/models/RolexContactPage'
import { RolexStore } from '@lib/kontent/models/RolexStore'
import { Box, Flex, theme } from '@gassan-ui'
import { Button, Link } from '@rolex-ui'
import { useLocalization } from '@components/Localization'
import { Page, pickSeoProps } from '@components/Page'
import RolexPage from '@components/_rolex/Page'
import { rem } from 'polished'
import Hero from '@components/_rolex/Hero'
import RetailerList from '@components/_rolex/RetailerList'
import RetailerMap from '@components/_rolex/RetailerMap'
import { AnimatePresence, motion } from 'framer-motion'
import ContactForm from '@components/_rolex/ContactForm'
import KeepExploring from '@components/_rolex/Sliders/KeepExploring'
import TextBlock from '@components/_rolex/TextBlock'

type Props = SingleItemResponse<RolexContactPage> & {
  retailers: MultipleItemResponse<RolexStore>
}

const RolexContact: FC<Props> = ({ item, modular_content, retailers }) => {
  const { messages, lang } = useLocalization()
  const keepExploringItems = item.elements.keep_exploring_slider_items.value.map(
    key => modular_content[key].elements
  )
  const [retailerView, setRetailerView] = useState<'list' | 'map'>('map')

  const handleGoToContactForm = () => {
    const availableEl = document.getElementById('contact')
    if (availableEl && availableEl.offsetTop) {
      window.scrollTo({ top: availableEl.offsetTop - 80, left: 0, behavior: 'smooth' })
    }
  }
  return (
    <Page {...pickSeoProps(item.elements)} slug={'rolex/contact'}>
      <RolexPage>
        <Box mb="5vh">
          <Hero
            sources={[
              getKontentImage(item.elements.rolex_hero__mobile?.value[0]?.url, { width: 640 }),
              getKontentImage(item.elements.rolex_hero__desktop?.value[0]?.url, { width: 1000 }),
              getKontentImage(item.elements.rolex_hero__desktop?.value[0]?.url, { width: 1680 })
            ]}
          />
        </Box>
        <TextBlock
          preTitle={messages.rolex.contact.officialRetailer}
          title={messages.rolex.contact.contactUs}
          description={messages.rolex.contact.contactDescription}
        >
          <Box mt={[rem(20), rem(30)]}>
            <Button onClick={() => handleGoToContactForm()}>{messages.rolex.cta.message}</Button>
          </Box>
        </TextBlock>
        <Box width={{ _: '90%', small: '80%' }} maxWidth={rem(1140)} mx="auto" mt="10vh">
          <Flex alignItems="center" mb={rem(20)}>
            <Link
              onClick={() => setRetailerView('map')}
              color={
                retailerView === 'map' ? theme.rolex.colors.shades[400] : theme.rolex.colors.primary
              }
              style={{ pointerEvents: retailerView === 'map' ? 'none' : 'all' }}
              mr="0.5rem"
            >
              {lang === 'nl' ? 'Bekijk kaart' : 'View map'}
            </Link>
            <svg height="11" width="11" fill={theme.rolex.colors.shades[900]} viewBox="0 0 11 11">
              <path d="m0 6.5v-1h11v1z" />
            </svg>
            <Link
              onClick={() => setRetailerView('list')}
              color={
                retailerView === 'list'
                  ? theme.rolex.colors.shades[400]
                  : theme.rolex.colors.primary
              }
              style={{ pointerEvents: retailerView === 'list' ? 'none' : 'all' }}
              ml="0.5rem"
            >
              {lang === 'nl' ? 'Bekijk lijst' : 'View list'}
            </Link>
          </Flex>
          <AnimatePresence exitBeforeEnter>
            <motion.div
              key={retailerView}
              initial={{ y: -5, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -5, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {retailerView === 'list' ? (
                <RetailerList {...retailers} />
              ) : (
                <RetailerMap {...retailers} />
              )}
            </motion.div>
          </AnimatePresence>
        </Box>
        <ContactForm
          showStores
          backgroundColor="white"
          subject="Vraag vanaf Rolex contact pagina"
        />
        <KeepExploring
          items={keepExploringItems}
          title={lang === 'nl' ? 'Blijf ontdekken' : 'Keep exploring'}
        />
      </RolexPage>
    </Page>
  )
}

export const getStaticProps: GetStaticProps = async ({ preview, params }) => {
  try {
    const langParam =
      params && params.lang
        ? typeof params.lang === 'string'
          ? params.lang
          : params.lang.pop()
        : 'nl'
    const language = langParam === 'sg' ? 'en' : langParam
    const content = await getPageByUrlSlug<RolexContactPage>({
      language,
      urlSlug: 'contact-page',
      preview
    })
    const retailers = await getAllRolexRetailers({ language, preview })
    return {
      props: {
        ...content,
        retailers: retailers as MultipleItemResponse<RolexStore>
      }
    }
  } catch (error) {
    throw error
  }
}

export const getStaticPaths = () => {
  return {
    fallback: false,
    paths: [{ params: { lang: 'nl' } }, { params: { lang: 'en' } }, { params: { lang: 'sg' } }]
  }
}

export default RolexContact
